import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/invites/${token}`);
        setInvite(data);
        setFullName(data.full_name || "");
      } catch (e) {
        setErr(formatApiErrorDetail(e.response?.data?.detail) || "Invite not found or expired");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords do not match"); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/invites/${token}/accept`, { token, password, full_name: fullName });
      if (data.token) localStorage.setItem("lf_token", data.token);
      await refresh();
      nav("/app");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Failed to accept");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-white border border-zinc-200 rounded-xl p-8 card-crisp">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-9 h-9 rounded-md bg-[#FF5A00] flex items-center justify-center"><Zap className="w-5 h-5 text-white" strokeWidth={2.5}/></div>
            <div className="font-heading font-bold text-xl">LeadForge</div>
          </div>
          {loading ? (
            <div className="text-center text-zinc-500 py-6">Loading invite…</div>
          ) : !invite ? (
            <div className="text-center py-6" data-testid="invite-error">
              <h1 className="font-heading text-xl font-bold text-rose-600 mb-2">Invite unavailable</h1>
              <p className="text-sm text-zinc-500">{err}</p>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold mb-1">Join {invite.business_name}</h1>
              <p className="text-zinc-500 text-sm mb-6">You've been invited as <span className="font-mono-accent px-1.5 py-0.5 rounded bg-orange-100 text-[#FF5A00]">{invite.role}</span></p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input value={invite.email} disabled className="bg-zinc-50" data-testid="invite-email"/>
                </div>
                <div>
                  <Label>Full name</Label>
                  <Input required value={fullName} onChange={(e)=>setFullName(e.target.value)} data-testid="invite-fullname-input"/>
                </div>
                <div>
                  <Label>Create password</Label>
                  <Input type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="invite-password-input"/>
                </div>
                <div>
                  <Label>Confirm password</Label>
                  <Input type="password" required value={confirm} onChange={(e)=>setConfirm(e.target.value)} data-testid="invite-confirm-input"/>
                </div>
                {err && <div className="text-sm text-rose-600" data-testid="invite-form-error">{err}</div>}
                <Button type="submit" disabled={busy} className="w-full bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift" data-testid="invite-accept-btn">
                  {busy ? "Creating account…" : "Accept & create account"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
